import { set, cloneDeep, pick } from 'lodash-es';

export interface ChangeLogType {
  name: string;
  hasDiff: boolean;
  path?: string[];
  ignore?: boolean;
  data: any;
}

export interface CourseAssets {
  images: {
    hasImageIDs?: boolean;
    hero: {
      legacy: string;
      caption: string;
      largeURL: string;
      thumbnail: string;
    };
    landing: { url: string; caption: string };
  };
  survey: { url: string };
  videos: { hero: { url: string; caption: string } };
  certificate: { url: string };
}

export interface CourseSettings {
  points: { perWorkshop: number; perCourseCompletion: number };
  enableAutoGrade: boolean;
  enableHelpAndSupport: boolean;
  enableLearningJournal: boolean;
}

export interface CourseEducators {
  instructors: string[];
  staff: string[];
}

export interface BasePayloadType {
  assets?: CourseAssets;
  settings?: CourseSettings;
}

export const getID = (x) => x.id;

export const splitPrimarySecondary = (vals) => ({
  primary: '',
  secondary: vals.map(getID),
});

export const selectPrimary = (selected, idx) => {
  const updated = [...selected];
  const primary = updated.splice(idx, 1)[0];

  updated.unshift(primary);

  return updated;
};

export const checkPrimaryDiff = (initial, curr) => {
  // If the first element is not the same the "primary" selection has changed
  if ((initial[0] && initial[0].id) !== (curr[0] && curr[0].id)) {
    return true;
  }

  // Otherwise just check for diffs like any other array of objects w/ids
  return checkObjectArrayDiff(initial, curr);
};

export const checkObjectArrayDiff = (initial, current) => {
  // Before looping over any of the arrays to check for diffs
  // compare static lenfth value (if length is different values are ipso facto different)
  if (initial && current && initial.length !== current.length) {
    return true;
  }

  const original = initial ? initial.map(getID) : [];
  const currentIDs = current ? current.map(getID) : [];

  return currentIDs
    .reduce((accumulator, currentElement) => {
      original.includes(currentElement) ? accumulator.push(true) : accumulator.push(false);

      return accumulator;
    }, [])
    .some((x) => !x);
};

export const getDefaultAssetsPayload = (assets: CourseAssets) => {
  const copy = cloneDeep(assets) as CourseAssets;
  copy.images.hasImageIDs = undefined;
  return copy;
};

export const getDefaultRelatedContent = (relatedContent) => {
  const copy = cloneDeep(relatedContent);

  copy.resources = copy.resources.map(getID);
  copy.courses = copy.courses.map(getID);

  return copy;
};

export const buildPayload = (diffLogs: ChangeLogType[], base: BasePayloadType) =>
  diffLogs.reduce((acc, value) => {
    if (value.ignore) {
      return acc;
    }

    if (value.hasDiff) {
      if (value.path) {
        set(acc, value.path, value.data);
      } else {
        acc[value.name] = value.data;
      }
    }

    return acc;
  }, base);

const STEP_KEYS = ['items', 'title', 'type', 'id'];

export const formatWorkshopData = (data) => {
  const WORKSHOP_KEYS = [
    'id',
    'title',
    'vanityURL',
    'sections',
    'percentage',
    'shortDescription',
    'startsAt',
    'publishAt',
    'duration',
  ];

  const payload = pick(data, WORKSHOP_KEYS);
  payload.sections = payload.sections.map((section) => ({
    ...section,
    steps: section.steps.map((step) => pick(step, STEP_KEYS)),
  }));

  if (!payload.vanityURL) {
    payload.vanityURL = payload.title.toLocaleLowerCase().split(' ').join('-');
  }

  return payload;
};

export const buildWorkshopPayload = (workshop) => {
  const PAYLOAD_KEYS = ['id', 'sections'];

  const payload = pick(workshop, PAYLOAD_KEYS);
  payload.sections = payload.sections.map((section) => ({
    ...section,
    steps: section.steps.map((step) => pick(step, STEP_KEYS)),
  }));

  return payload;
};
